import React from 'react';
import { useState } from 'react';
import { Send, MapPin, Phone, Mail } from 'lucide-react';
import RevealOnScroll from '../WrapComponents/RevealOnScroll.jsx';
import PageTransition from '../WrapComponents/PageTransition.jsx';
import axiosInstance from '../Axiosintances/Axiosinstance.jsx';
import GoogleMapComponent from '../WrapComponents/GoogleMapComponent.jsx';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));

    // Clear error for this field when user types something
    if (value.trim() !== "" && errors[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    const newErrors = {
      name: formData.name.trim() ? "" : "Name is required",
      email: formData.email.trim() ? "" : "Email is required",
      phone: formData.phone.trim() ? "" : "Phone number is required",
      message: formData.message.trim() ? "" : "Message is required",
    };

    // If any field has an error, show messages and do not submit
    if (
      newErrors.name ||
      newErrors.email ||
      newErrors.phone ||
      newErrors.message
    ) {
      setErrors(newErrors);
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await axiosInstance.post(
        "/service/api/v1/public_contacts",
        formData
      );
      console.log("Success:", response.data);
      setShowSuccessModal(true);

      // Reset form after submission
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
      setErrors({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      // Optional: Add error handling UI if needed
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <PageTransition>
      <div className="bg-gray-50 dark:bg-gray-950 min-h-[85vh] transition-colors duration-300  min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <RevealOnScroll className="bg-white dark:bg-gray-900 rounded-[2.5rem] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">

              {/* Form Section */}
              <div className="p-8 md:p-12 lg:p-16">
                <h2 className="text-3xl font-bold mb-4 tracking-tight text-gray-900 dark:text-white">
                  Contact Us
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mb-10 text-lg">
                  Feel free to reach out to us for inquiries about our services, partnerships, or any other questions you might have. We're here to help!
                </p>

                <form className="space-y-6">
                  <div className="group">
                    <label
                      htmlFor="name"
                      className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 group-focus-within:text-black dark:group-focus-within:text-white transition-colors"
                    >
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-6 py-4 rounded-xl bg-gray-50 dark:bg-gray-800 border-2 border-transparent focus:border-black dark:focus:border-white focus:bg-white dark:focus:bg-gray-900 focus:ring-0 text-gray-900 dark:text-white transition-all duration-300 outline-none placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="John Doe"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                    )}
                  </div>

                  <div className="group">
                    <label
                      htmlFor="email"
                      className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 group-focus-within:text-black dark:group-focus-within:text-white transition-colors"
                    >
                      Your Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-6 py-4 rounded-xl bg-gray-50 dark:bg-gray-800 border-2 border-transparent focus:border-black dark:focus:border-white focus:bg-white dark:focus:bg-gray-900 focus:ring-0 text-gray-900 dark:text-white transition-all duration-300 outline-none placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="john@example.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                    )}
                  </div>

                  <div className="group">
                    <label
                      htmlFor="phone"
                      className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 group-focus-within:text-black dark:group-focus-within:text-white transition-colors"
                    >
                      Your Phone Number
                    </label>
                    <input
                      type="text"
                      id="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-6 py-4 rounded-xl bg-gray-50 dark:bg-gray-800 border-2 border-transparent focus:border-black dark:focus:border-white focus:bg-white dark:focus:bg-gray-900 focus:ring-0 text-gray-900 dark:text-white transition-all duration-300 outline-none placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="+91 **********"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                    )}
                  </div>

                  <div className="group">
                    <label
                      htmlFor="message"
                      className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 group-focus-within:text-black dark:group-focus-within:text-white transition-colors"
                    >
                      Your Message
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-6 py-4 rounded-xl bg-gray-50 dark:bg-gray-800 border-2 border-transparent focus:border-black dark:focus:border-white focus:bg-white dark:focus:bg-gray-900 focus:ring-0 text-gray-900 dark:text-white transition-all duration-300 outline-none resize-none placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="How can we help you?"
                    ></textarea>
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full bg-black dark:bg-white text-white dark:text-black font-bold py-4 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-200 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg flex justify-center items-center gap-2 group mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                    {!isSubmitting && <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />}
                  </button>
                </form>
              </div>

              {/* Info & Map Section */}
              <div className="bg-gray-100/50 dark:bg-gray-800/50 p-8 md:p-12 lg:p-16 border-l border-gray-100 dark:border-gray-800 flex flex-col justify-between">
                <div className="mb-12">
                  <h3 className="text-xl font-bold mb-8 text-gray-900 dark:text-white">Our Office</h3>

                  <div className="space-y-6">

                    {/* Address */}
                    <div className="flex items-start gap-4 group cursor-default">
                      <div className="p-3 bg-white dark:bg-gray-700 rounded-lg shadow-sm group-hover:scale-110 transition-transform duration-300">
                        <MapPin className="w-5 h-5 text-black dark:text-white" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mt-2">
                        PROJECT LOCK
                        39/2475-B1, Suite J80,
                        LR Towers,<br /> SJRRA 104,
                        South Janatha Road, Palarivattom,
                        <br /> Kochi, Kerala 682025,
                      </p>
                    </div>

                    {/* Email */}
                    <div className="flex items-center gap-4 group">
                      <div className="p-3 bg-white dark:bg-gray-700 rounded-lg shadow-sm group-hover:scale-110 transition-transform duration-300">
                        <Mail className="w-5 h-5 text-black dark:text-white" />
                      </div>
                      <a
                        href="mailto:info@project-lock.com"
                        className="text-gray-600 dark:text-gray-300 text-sm hover:text-black dark:hover:text-white hover:underline transition-colors"
                      >
                        info@project-lock.com
                      </a>
                    </div>

                    {/* Phone Number */}
                    <div className="flex items-center gap-4 group">
                      <div
                        onClick={() => window.location.href = "tel:+917356288295"}
                        className="p-3 bg-white dark:bg-gray-700 rounded-lg shadow-sm group-hover:scale-110 transition-transform duration-300 cursor-pointer"
                      >
                        <Phone className="w-5 h-5 text-black dark:text-white" />
                      </div>

                      <a
                        href="tel:+917356288295"
                        className="text-gray-600 dark:text-gray-300 text-sm hover:text-black dark:hover:text-white hover:underline transition-colors"
                      >
                        +91 73562 88295
                      </a>
                    </div>

                    {/* WhatsApp Chat */}
                    <div className="flex items-center gap-4 group">
                      <div
                        onClick={() => window.open("https://wa.me/917356288295", "_blank")}
                        className="p-3 bg-white dark:bg-green-700 rounded-lg shadow-sm group-hover:scale-110 transition-transform duration-300 cursor-pointer"
                      >
                        <img
                          src="https://cdn-icons-png.flaticon.com/512/733/733585.png"
                          className="w-5 h-5"
                          alt="WhatsApp"
                        />
                      </div>

                      <a
                        href="https://wa.me/917356288295"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 dark:text-gray-300 text-sm hover:text-green-600 dark:hover:text-green-400 hover:underline transition-colors"
                      >
                        Chat on WhatsApp
                      </a>
                    </div>

                  </div>
                </div>

                {/* <div className="w-full h-80 rounded-[1.5rem] mb-10 shadow-lg border border-white dark:border-gray-700 relative group">
                  <GoogleMapComponent />
                </div> */}
              </div>

            </div>
          </RevealOnScroll>
        </div>
      </div>
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <RevealOnScroll className="bg-white dark:bg-gray-900 rounded-[2rem] p-8 md:p-12 max-w-lg w-full shadow-2xl border border-gray-100 dark:border-gray-800 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-400 to-emerald-500"></div>

            <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>

            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Message Sent!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
              Thank you for reaching out. Our team will contact you soon to discuss your requirements.
            </p>

            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full bg-black dark:bg-white text-white dark:text-black font-bold py-4 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-200 transition-all transform hover:scale-[1.05] active:scale-95 shadow-lg"
            >
              Close
            </button>
          </RevealOnScroll>
        </div>
      )}
    </PageTransition>
  );
};

export default Contact;
